import Config from "react-native-config";
import Constants, { APIService } from "../utilities/Constants";
import { apiGet, apiPatch, apiPost } from "./utils/apiUtils";
import { Platform } from "react-native";
import Storage from "../utilities/storage/StorageFunctions";
import { AxiosRequestHeaders } from "axios";
import qs from "qs";
import { getUserIdFromToken, Logger } from "../utilities/HelperFunctions";
import { ErrorHandler } from "./utils/errorUtils";

import { clearSessionAndLogout } from "../utilities/AuthHelper";
import { navigationRef } from "../../src/navigation/NavigationServices";
import { performLogout } from "../../src/providers/AuthProvider";
import { InitialFormFieldsType } from "../hooks/register/type";
import { messages } from "../../src/resources/strings";
const URLENCODED_CONTENT_TYPE = "application/x-www-form-urlencoded";

export const getUserDetailsFromId = async (id: string) => {
  const customErrorHandlers: ErrorHandler[] = [
    {
      case: 401,
      onError: () => {
        clearSessionAndLogout(navigationRef?.current, performLogout);
      },
    },
  ];

  const response = await apiGet<any>(
    APIService.harmony,
    `/user/${id}`,
    {
      isUserAuthRequired: true,
    },
    {},
    customErrorHandlers
  );

  if (!response?.user) {
    return {
      errorCode: messages.error.somthingWrong,
      message: "User details not found",
    };
  }

  const clientId =
    Platform.OS === "web"
      ? process.env.REACT_APP_EXZEO_ID_CLIENT_ID
      : Config.REACT_APP_EXZEO_ID_CLIENT_ID;

  const hasAccess =
    response.user.client.find((item: any) => item.complex_id === clientId) !==
    undefined;

  return {
    response: { user: response.user, hasAccess },
    errorCode: messages.error.noError,
  };
};

//API :: Register user in Nexus environment
export const registerUser = async (email: string) => {
  if (!email) {
    return {
      errorCode: messages.error.somthingWrong,
      message: "Email is required",
    };
  }

  let object = {
    emailId: email,
  };

  const params = JSON.stringify(object);
  const response = await apiPost(
    APIService.notificationService,
    "/user/register",
    params,
    { isUserAuthRequired: true }
  );
  if (response.status === 200) {
    return {
      response: "Successfully registered user",
      errorCode: messages.error.noError,
    };
  } else {
    console.log("User Registration error:", JSON.stringify(response));
    return {
      errorCode: messages.error.somthingWrong,
    };
  }
};

//API :: Add/Update device token
export const registerDeviceToken = async (
  email: string,
  deviceToken: string,
  deviceTokenUserId?: string
) => {
  if (!email || !deviceToken) {
    return {
      errorCode: messages.error.somthingWrong,
      message: "Email and device token are required",
    };
  }

  let object = {
    emailId: email,
    deviceToken: deviceToken,
    platform: Platform.OS,
    ...(deviceTokenUserId && { id: deviceTokenUserId }),
  };

  const params = JSON.stringify(object);
  const response = await apiPost(
    APIService.notificationService,
    "/device/register",
    params,
    { isUserAuthRequired: true }
  );

  if (response.status === 200) {
    await Storage.storeData(
      Constants.LocalStoreConstant.DEVICE_TOKEN,
      response.data.deviceToken
    );
    await Storage.storeData(
      Constants.LocalStoreConstant.DEVICE_TOKEN_USER_ID,
      `${response.data.deviceId}`
    );

    await Storage.storeData(
      Constants.LocalStoreConstant.DEVICE_UPLOADED,
      response.data.deviceToken
    );

    return {
      response: "Successfully uploaded device token",
      errorCode: messages.error.noError,
    };
  } else {
    return {
      errorCode: messages.error.somthingWrong,
    };
  }
};

//To check if token is still valid or not --
export const introspectionAPI = async () => {
  let paramToken = await Storage.getData(
    Constants.LocalStoreConstant.ACCESS_TOKEN
  );
  if (paramToken?.includes("Bearer ")) {
    paramToken = paramToken?.split(" ")[1];
  }
  let paramData = {
    client_id: `${
      Platform.OS == "web"
        ? process.env.REACT_APP_EXZEO_ID_CLIENT_ID
        : Config.REACT_APP_EXZEO_ID_CLIENT_ID
    }`,
    token: paramToken,
  };
  const params = qs.stringify(paramData);
  const header = {
    "Content-Type": URLENCODED_CONTENT_TYPE,
  };
  const response = await apiPost(
    APIService.exzeo,
    "/oidc/token/introspection",
    params,
    { isUserAuthRequired: true },
    [],
    header as AxiosRequestHeaders
  );

  if (response.status === 200) {
    const data = response.data;
    if (!data?.active) {
      return {
        response: false,
        errorCode: messages.error.resetPasswordLogout,
      };
    }
    return {
      response: data.active,
      errorCode: messages.error.noError,
    };
  } else {
    return {
      errorCode: messages.error.somthingWrong,
    };
  }
};

//API :: Include user in client group in Exzeo ID
export const includeUserFromClientGroup = async (
  userId: string,
  clientId: string
) => {
  const params = JSON.stringify({
    id: `${userId}`,
  });

  const response = await apiPatch(
    APIService.harmony,
    `/client/${clientId}/group/include/user`,
    params,
    {
      isUserAuthRequired: true,
      isAppAccessTokenRequired: false,
      scope: "write:user:create",
      isForPDF: false,
    }
  );

  if (response.status === 200) {
    return {
      response: null,
      errorCode: messages.error.noError,
    };
  } else {
    return {
      errorCode: messages.error.somthingWrong,
    };
  }
};

// Post User details to Create User in Exzeo-Id
export const grantAppAccess = async (userId: string, clientId: string) => {
  try {
    // Populate params to create user
    const params = JSON.stringify({
      id: `${userId}`,
      client_id: `${clientId}`,
      send_notification: false,
    });

    // Initialize axios instance with the required values
    const response = await apiPost(APIService.harmony, `/user/grant`, params, {
      isUserAuthRequired: true,
      isAppAccessTokenRequired: false,
      scope: "write:user:create",
      isForPDF: false,
    });
    if (response.status === 200) {
      return {
        response: null,
        errorCode: messages.error.noError,
      };
    } else {
      return {
        errorCode: messages.error.somthingWrong,
      };
    }
  } catch (error) {
    if (error == messages.error.networkError) {
      return {
        error: error,
        errorCode: messages.error.networkError,
      };
    } else {
      return {
        error: error,
        errorCode: messages.error.trycatchError,
      };
    }
  }
};

//hit GrantAccess Api On GetPolicy 401 Error
export const hitGrantAccessApiOnGetPolicy401Error = async (
  grantAccessApiCounter: number
) => {
  if (grantAccessApiCounter == 0) {
    const tokenLS = await Storage.getData(
      Constants.LocalStoreConstant.ACCESS_TOKEN
    );
    // get user id from token
    const userId = getUserIdFromToken(tokenLS);
    const clientId =
      Platform.OS == "web"
        ? process.env.REACT_APP_HARMONY_API_CLIENT_ID
        : Config.REACT_APP_HARMONY_API_CLIENT_ID;
    await includeUserFromClientGroup(userId, clientId ?? "");
    await grantAppAccess(userId, clientId ?? "");
    return {
      errorCode: messages.error.noHarmonyUserFound,
    };
  } else {
    return {
      errorCode: messages.error.noHarmonyUserFound,
    };
  }
};

//API :: Delete device token
export const deleteDeviceToken = async (deviceToken: string) => {
  const body = { deviceToken: deviceToken };
  const parsedBody = { deviceToken: body?.deviceToken?.replace(/"/g, "") };
  const response = await apiPost(
    APIService.notificationService,
    `/device/delete`,
    parsedBody
  );
  if (response.status === 200) {
    Logger("Device token deleted");
  } else {
    Logger("Device token deletion failed" + JSON.stringify(response));
  }
};

//API :: Register
// To get the sub value of a user by email
export const getUserIdFromEmail = async (payload: InitialFormFieldsType) => {
  // Initialize axios instance  with email id

  const response = await apiPost(APIService.harmony, `/user/sub`, payload, {
    scope: "read:user:all",
    isForPDF: false,
    isUserAuthRequired: true,
    isAppAccessTokenRequired: false,
  });

  if (response.status === 200) {
    const data = response.data;
    return {
      response: data,
      errorCode: messages.error.noError,
    };
  } else {
    return {
      errorCode: response?.message || messages.error.somthingWrong,
    };
  }
};
//API :: Register
// Post User details to Create User in Exzeo-Id
export const createUser = async (userObject: any) => {
  // Populate params to create user
  const params = JSON.stringify({
    first_name: userObject.firstName,
    last_name: userObject.lastName,
    email: userObject.email,
    password: userObject.password,
    phone: userObject.phone,
    phone_verified: true,
    email_verified: false,
  });

  const response = await apiPost(
    APIService.harmony,
    `/user?verifyEmail=true`,
    params,
    { scope: "read:user:all", isForPDF: false, isUserAuthRequired: true }
  );
  if (response.status === 200) {
    return {
      response: response.data,
      errorCode: messages.error.noError,
    };
  } else {
    return {
      errorCode: messages.error.somthingWrong,
    };
  }
};

//API :: Delete user account
export const deleteUserAccount = async () => {
  const response = await apiPost(
    APIService.notificationService,
    `/user/delete`,
    {},
    {}
  );

  if (response.status === 200) {
    return {
      response: null,
      errorCode: messages.error.noError,
    };
  } else {
    return {
      errorCode: messages.error.somthingWrong,
    };
  }
};
//API :: Exclude user in client group in Exzeo ID
export const excludeUserFromClientGroup = async (
  userId: string,
  clientId: string
) => {
  const headerConfig = {
    scope: "write:user:update",
    isForPDF: false,
    isUserAuthRequired: true,
  };

  // Populate params to create user
  const params = JSON.stringify({
    id: `${userId}`,
  });

  const response = await apiPatch(
    APIService.harmony,
    `/client/${clientId}/group/exclude/user`,
    params,
    headerConfig
  );
  if (response.status === 200) {
    return {
      response: null,
      errorCode: messages.error.noError,
    };
  } else {
    return {
      errorCode: messages.error.somthingWrong,
    };
  }
};

export const handleError403Introspection = async (
  errorToReturnOnSuccess = messages.error.trycatchError
) => {
  const introspectionResponse = await introspectionAPI();
  if (introspectionResponse.response) {
    return {
      errorCode: errorToReturnOnSuccess,
    };
  } else if (introspectionResponse.errorCode == messages.error.networkError) {
    return {
      error: messages.error.networkError,
      errorCode: messages.error.networkError,
    };
  } else {
    return {
      errorCode: messages.error.resetPasswordLogout,
    };
  }
};

export const handleError500 = async(errorMsg:string) => {
  return errorMsg
}